import { useCallback, useEffect, useState } from "react";
import DataTable, { Column, FilterOption } from "../../components/commons/DataTable";
import ReportAction from "../../components/partials/report/ReportsAction";
import type { Report, ReportType } from "../../services/webServices/report/types";
import { getReports } from "../../services/webServices/report/get";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

const PAGE_SIZE = 12;

const filterOptions: FilterOption[] = [
  { value: "article", label: "مقالات" },
  { value: "course", label: "دوره های" },
  { value: "post", label: "پست ها" },
  { value: "user", label: "کاربران" },
];

export default function Report() {

  const [data, setData] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<ReportType>("article");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchReport = useCallback(async () => {

    if (isFirstLoad) showLoading();
    setIsLoading(true);

    const response = await getReports({ type: filterValue });

    if (Array.isArray(response)) {
      setData(response);

      if (response.length < PAGE_SIZE) {
        const calculatedTotal =
          (currentPage - 1) * PAGE_SIZE + response.length;
        setTotalItems(calculatedTotal);
      } else {
        setTotalItems(currentPage * PAGE_SIZE + 1);
      }
    } else {
      setData([]);
      setTotalItems(0);
    }

    setIsLoading(false);
    if (isFirstLoad) hideLoading();
  }, [currentPage, filterValue]);


  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (value: string) => {
    setFilterValue(value as ReportType);
    setCurrentPage(1);
  };

  const handleRenderActions = (row: Report) => {
    return (
      <ReportAction
        report={row}
        onActionSuccessed={() => fetchReport()}
      />
    );
  };

  const REPORT_TARGET_HEADER: Record<ReportType, string> = {
    user: "کاربر",
    post: "پست",
    course: "دوره",
    article: "مقاله"
  }

  const REPORT_SUBJECT_ACCESSOR: Record<number, string> = {
    1: "محتوای غیراخلاقی",
    2: "کلاهبرداری",
    3: "هرزنامه",
    4: filterValue === "course" ? "قیمت غیرمنصفانه" : filterValue === "user" ? "رفتارهای غیرقانونی" : "محتوای غیرآموزشی",
    5: "سایر موارد",
  };

  const columns: Column<Report>[] = [
    {
      key: "target",
      header: REPORT_TARGET_HEADER[filterValue],
      align: "right",
      sortable: true,
      width: "35%",
      accessor: (row) => row.target,
    },
    {
      key: "subject",
      header: "موضوع گزارش",
      align: "right",
      sortable: true,
      width: "20%",
      accessor: (row) => REPORT_SUBJECT_ACCESSOR[row.subject],
    },
    {
      key: "updated_at",
      header: "تاریخ گزارش",
      align: "right",
      sortable: true,
      width: "15%",
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <span>
            {row.updated_at
              ? new Date(row.updated_at).toLocaleDateString("fa-IR")
              : "بدون تاریخ"}
          </span>
        </div>
      ),
    },
    {
      key: "user",
      header: "کاربر گزارش دهنده",
      align: "right",
      sortable: true,
      width: "15%",
      accessor: (row) => row.user,
    }
  ];

  return (
    <DataTable
      title="لیست گزارشات"
      data={data}
      columns={columns}
      loading={isLoading}
      loadingText="در حال بارگذاری گزارشات..."
      emptyText="گزارشی یافت نشد"

      filterable={true}
      filterPlaceholder=""
      filterOptions={filterOptions}
      filterValue={filterValue}
      onFilterChange={handleFilterChange}

      pagination={true}
      serverSidePagination={true}
      currentPage={currentPage}
      onPageChange={handlePageChange}
      totalItems={totalItems}
      pageSize={PAGE_SIZE}

      renderActions={handleRenderActions}
    />
  );
};